import React from 'react';
import { CheckCircle, Award, Users, Globe } from 'lucide-react';
import { FadeIn } from './FadeIn';

export const TrustSection: React.FC = () => {
    const stats = [
        {
            icon: CheckCircle,
            number: '50+',
            label: 'Projects Completed',
            description: 'Successfully delivered across industries'
        },
        {
            icon: Users,
            number: '40+',
            label: 'Happy Clients',
            description: 'Businesses transformed globally'
        },
        {
            icon: Globe,
            number: '15+',
            label: 'Countries Served',
            description: 'International reach & expertise'
        },
        {
            icon: Award,
            number: '100%',
            label: 'Client Satisfaction',
            description: 'Dedicated to excellence'
        }
    ];

    const features = [
        'Premium Quality Designs',
        'On-Time Delivery Guaranteed',
        'Post-Launch Support Included',
        'SEO & Performance Optimized',
        'Mobile-First Approach',
        'Secure & Scalable Solutions'
    ];

    return (
        <section className="py-24 px-6 bg-neutral-900/30">
            <div className="max-w-6xl mx-auto">
                {/* Stats Grid */}
                <FadeIn>
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Trusted by Businesses Worldwide
                        </h2>
                        <p className="text-neutral-400 max-w-2xl mx-auto">
                            Proven track record of delivering exceptional digital solutions
                        </p>
                    </div>
                </FadeIn>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
                    {stats.map((stat, index) => (
                        <FadeIn key={stat.label} delay={index * 0.1}>
                            <div className="text-center p-6 rounded-2xl bg-neutral-900/50 border border-neutral-800 hover:border-neutral-700 transition-all group">
                                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                                    <stat.icon className="text-blue-400" size={24} />
                                </div>
                                <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                                <div className="text-sm font-medium text-neutral-300 mb-1">{stat.label}</div>
                                <div className="text-xs text-neutral-500">{stat.description}</div>
                            </div>
                        </FadeIn>
                    ))}
                </div>

                {/* Trust Features */}
                <FadeIn delay={0.4}>
                    <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 md:p-12">
                        <h3 className="text-2xl font-semibold text-white mb-8 text-center">
                            Why Choose Refine Studio?
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {features.map((feature, index) => (
                                <FadeIn key={feature} delay={0.5 + index * 0.05}>
                                    <div className="flex items-center gap-3 text-neutral-300">
                                        <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                                            <CheckCircle className="text-green-500" size={14} />
                                        </div>
                                        <span className="text-sm">{feature}</span>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </FadeIn>

                {/* Trust Badges */}
                <FadeIn delay={0.8}>
                    <div className="mt-12 text-center">
                        <div className="inline-flex flex-wrap justify-center gap-6 items-center text-neutral-500 text-sm">
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-green-500" />
                                <span>Verified Business</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-green-500" />
                                <span>Secure Payments</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-green-500" />
                                <span>NDA Protected</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-green-500" />
                                <span>24/7 Support</span>
                            </div>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
};
